/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useRef, useCallback } from "react";

/**
 * Context for mapping assistant-ui client message IDs to database UUIDs.
 *
 * When messages are loaded from history, they already have database UUIDs.
 * When new messages are created, assistant-ui generates short client IDs,
 * but we need the database UUID for features like feedback.
 *
 * This context stores the mapping so we can look up the database ID.
 */

type MessageIdMapping = {
  /** Map a client ID to its database UUID */
  setMapping: (clientId: string, databaseId: string) => void;
  /** Get the database UUID for a client ID (returns clientId if no mapping exists) */
  getDatabaseId: (clientId: string) => string;
  /** Check if an ID looks like a database UUID */
  isUUID: (id: string) => boolean;
  /** Check if a client ID has already been mapped to a database UUID */
  hasMapping: (clientId: string) => boolean;
  /** Clear all mappings (call when switching documents/conversations) */
  clearMappings: () => void;
};

const MessageIdMappingContext = createContext<MessageIdMapping | null>(null);

export const MessageIdMappingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Using ref to avoid re-renders when mapping changes
  const mappingRef = useRef<Map<string, string>>(new Map());

  const setMapping = useCallback((clientId: string, databaseId: string) => {
    mappingRef.current.set(clientId, databaseId);
  }, []);

  const isUUID = useCallback((id: string): boolean => {
    // UUID format: 8-4-4-4-12 hexadecimal characters
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
  }, []);

  const getDatabaseId = useCallback(
    (clientId: string): string => {
      // If it's already a UUID, return as-is (loaded from history)
      if (isUUID(clientId)) {
        return clientId;
      }
      // Otherwise, look up in mapping
      const databaseId = mappingRef.current.get(clientId);
      if (databaseId) {
        return databaseId;
      }
      // No mapping found - return original (will cause error if used with UUID column)
      // Note: This is expected for messages loaded from history (already have UUIDs)
      return clientId;
    },
    [isUUID],
  );

  const hasMapping = useCallback(
    (clientId: string): boolean => {
      // UUIDs are already "mapped" (from history)
      if (isUUID(clientId)) {
        return true;
      }
      return mappingRef.current.has(clientId);
    },
    [isUUID],
  );

  const clearMappings = useCallback(() => {
    mappingRef.current.clear();
  }, []);

  const value = React.useMemo(
    () => ({ setMapping, getDatabaseId, isUUID, hasMapping, clearMappings }),
    [setMapping, getDatabaseId, isUUID, hasMapping, clearMappings],
  );

  return (
    <MessageIdMappingContext.Provider value={value}>{children}</MessageIdMappingContext.Provider>
  );
};

export const useMessageIdMapping = (): MessageIdMapping => {
  const context = useContext(MessageIdMappingContext);
  if (!context) {
    throw new Error("useMessageIdMapping must be used within MessageIdMappingProvider");
  }
  return context;
};

/**
 * Hook to get the database message ID from an assistant-ui message ID.
 * Returns null if the ID cannot be converted to a valid database UUID.
 */
export const useDatabaseMessageId = (clientId: string | undefined): string | null => {
  const { getDatabaseId, isUUID } = useMessageIdMapping();

  if (!clientId) {
    return null;
  }

  const databaseId = getDatabaseId(clientId);

  // Only return if it's a valid UUID
  if (isUUID(databaseId)) {
    return databaseId;
  }

  return null;
};
